import { useState, type FormEvent } from 'react';
import { Banner, Button, Input, Modal } from '@/components/ds-dlp';
import { toast } from '@/lib/toast';
import { accountApi, passwordProblem } from '@/api/account';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called after the change; the server has signed out every session. */
  onChanged: () => void;
  /** An administrator set the password (new account or reset): say why the dialog opened. */
  required?: boolean;
}

// The account menu's "Change password". Also opens on its own after an
// administrator reset the password (user.must_change_password).
export function ChangePasswordDialog({ open, onClose, onChanged, required = false }: Props) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const close = () => {
    if (loading) return;
    setCurrent('');
    setNext('');
    setConfirm('');
    setError('');
    onClose();
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const problem = passwordProblem(next, confirm);
    if (problem) {
      setError(problem);
      return;
    }
    if (next === current) {
      setError('Choose a password different from the current one.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await accountApi.changePassword(current, next);
      toast.success('Password changed', { description: 'Sign in again with the new password.' });
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change the password');
    }
    setLoading(false);
  };

  return (
    <Modal
      open={open}
      onClose={close}
      showCloseButton={!loading}
      size="sm"
      title="Change password"
      footer={
        <>
          <Button variant="secondary" onClick={close} disabled={loading}>
            {required ? 'Later' : 'Cancel'}
          </Button>
          <Button type="submit" form="change-password-form" variant="primary" loading={loading} disabled={!current || !next || !confirm}>
            Change password
          </Button>
        </>
      }
    >
      <form id="change-password-form" onSubmit={submit} className="flex flex-col gap-4">
        {required ? (
          <Banner variant="warning">An administrator set your password. Choose a new one that only you know.</Banner>
        ) : null}
        {error ? <Banner variant="danger">{error}</Banner> : null}
        <Input
          size="sm"
          label="Current password"
          type="password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          autoComplete="current-password"
          autoFocus
        />
        <Input
          size="sm"
          label="New password"
          type="password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          autoComplete="new-password"
          help="At least 8 characters. You'll sign in again on every device."
        />
        <Input
          size="sm"
          label="Confirm new password"
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          autoComplete="new-password"
        />
      </form>
    </Modal>
  );
}
